import {
  Code,
  DeliveryDateModalQuery,
  DeliveryDateModalWithCourierQuery
} from '@/gql/graphql'
import { Language } from '@/packs/localisation'
import { format, parseISO } from 'date-fns'
import React, {
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  SetStateAction
} from 'react'

import { Expand } from '@/components/elements/atoms/Animated/Animated'
import { Button } from '@/components/elements/atoms/Button'
import Card from '@/components/elements/atoms/Card/Card'
import FlatButton from '@/components/elements/atoms/FlatButton/FlatButton'
import { Notification } from '@/components/elements/atoms/InPageNotification'
import MultiLineTextField from '@/components/elements/atoms/MultiLineTextField/MultiLineTextField'
import Text from '@/components/elements/atoms/Text'
import CourierSelector from '@/components/elements/molecules/CourierSelector/CourierSelector'

import STYLES from './DeliveryDateSection.module.sass'

import { DeliveryDetailsFormKey } from '../../types'
import { InputWithString } from '../InputField/InputField'
import MobileNumberInputField from '../InputField/MobileNumberInputField'
import DeliveryDateModal from './DeliveryDateModal'
import DeliveryDateModalWithCourier from './DeliveryDateModalWithCourier/DeliveryDateModalWithCourier'
import DeliveryFeeInfo from './DeliveryFeeInfo'

type CourierCalendarDates = DeliveryDateModalWithCourierQuery['calendarDates']

type CalendarDates =
  | DeliveryDateModalQuery['calendarDates']
  | CourierCalendarDates

type DeliveryArea = DeliveryDateModalWithCourierQuery['deliveryAreaOptions'][0]

type Props = {
  namespace: string
  copyContext: string
  titleText: string
  visible: boolean
  valid: boolean
  addressDetailsValid: boolean
  editSection: (
    event:
      | MouseEvent<HTMLDivElement | MouseEvent | HTMLElement>
      | KeyboardEvent<HTMLDivElement | KeyboardEvent | HTMLElement>
      | undefined
  ) => void
  toggleDeliveryDateModal: () => void
  showDeliveryDateModal: boolean

  selectedDeliveryDate: {
    value: string
    selectedDeliveryArea:
      | DeliveryDateModalWithCourierQuery['deliveryAreaOptions'][0]
      | null
  }
  deliveryFee: number
  postcode: {
    value: string
  }
  user: {
    firstDeliverableDate: {
      date: string
    }
  }
  shouldSeeCourierSelection: boolean
  shouldSeeDeliveryNotes: boolean
  shouldSeeRequiredPhoneNumber: boolean
  shouldSeeOptimisedCourierSelection: boolean
  shippingCountryCode: Code
  preferredLanguage: Language
  calendarDates: CalendarDates
  setCalendarDates: Dispatch<SetStateAction<CalendarDates>>
  deliveryDateData?: {
    calendarDates: CalendarDates
  }
  deliveryDateWithCourierLoading: boolean
  deliveryDateWithCourierData?: DeliveryDateModalWithCourierQuery
  deliveryDatesLoading: boolean
  requiresPaymentDetailsOnCheckout: boolean
  continueButtonClick: () => void
  updateDeliveryDetails: (
    fieldName: DeliveryDetailsFormKey,
    value: string
  ) => void
  validateInputField: (fieldName: DeliveryDetailsFormKey, value: string) => void
  mobileNumber: InputWithString
  deliveryNotes: InputWithString
  courierInfoCopy: () => string
  calendarIcon: string
  rightArrow: string
  dateLocale: Locale
  t: (key: string) => string
  setSelectedCarrier: (carrier: DeliveryArea) => void
  setSelectedDate: (date: Date | null) => void
}

const DeliveryDateOptimisedCourier = ({
  namespace,
  copyContext,
  titleText,
  visible,
  valid,
  addressDetailsValid,
  editSection,
  toggleDeliveryDateModal,
  showDeliveryDateModal,
  selectedDeliveryDate,
  deliveryFee,
  postcode,
  user,
  shouldSeeCourierSelection,
  shouldSeeDeliveryNotes,
  shouldSeeRequiredPhoneNumber,
  shouldSeeOptimisedCourierSelection,
  shippingCountryCode,
  preferredLanguage,
  calendarDates,
  setCalendarDates,
  deliveryDateData,
  deliveryDateWithCourierLoading,
  deliveryDateWithCourierData,
  deliveryDatesLoading,
  requiresPaymentDetailsOnCheckout,
  continueButtonClick,
  updateDeliveryDetails,
  validateInputField,
  mobileNumber,
  deliveryNotes,
  courierInfoCopy,
  calendarIcon,
  rightArrow,
  dateLocale,
  setSelectedCarrier,
  setSelectedDate,
  t
}: Props): JSX.Element => {
  return (
    <section
      className={`delivery-date-section section ${
        visible ? 'section--visible' : 'section--hidden'
      }`}
    >
      <Card shadow>
        <div className="card-content">
          <div className="card-title">
            <Text
              namespace={namespace}
              text={`${copyContext}.${titleText}`}
              element="h2"
              variant="display20"
              align="left"
              margin={false}
            />
            {!visible && valid && addressDetailsValid && (
              <FlatButton
                variant="yellow200"
                onClick={editSection}
                identifier="Delivery Date Edit"
                screenIdentifier="checkout_page"
                text={{
                  namespace: namespace,
                  text: 'edit_button'
                }}
              />
            )}
          </div>
          {!visible && valid && (
            <Text
              colour="brandBlue400"
              translate={false}
              align="left"
              text={format(
                parseISO(selectedDeliveryDate.value),
                'EEEE MMMM do yyy',
                {
                  locale: dateLocale
                }
              )}
            />
          )}
          <Expand show={visible} margin={{ top: 0.8 }}>
            <DeliveryFeeInfo
              deliveryFee={deliveryFee}
              relevantPostcode={postcode.value}
            />
            <div>
              {shouldSeeCourierSelection &&
                selectedDeliveryDate.selectedDeliveryArea && (
                  <div className={STYLES.courierWrapper}>
                    <Text
                      namespace={namespace}
                      text={`${copyContext}.courier_selection.title`}
                      variant="display16"
                      align="left"
                    />
                    <Text
                      namespace={namespace}
                      text={`${copyContext}.courier_selection.text`}
                      align="left"
                    />
                    <CourierSelector
                      deliveryAreaOptions={
                        deliveryDateWithCourierData?.deliveryAreaOptions ?? []
                      }
                      selectedCarrier={
                        selectedDeliveryDate.selectedDeliveryArea
                      }
                      setSelectedCarrier={setSelectedCarrier}
                      setCalendarDates={setCalendarDates}
                      selectedDate={parseISO(selectedDeliveryDate.value)}
                      setSelectedDate={setSelectedDate}
                    />
                  </div>
                )}

              <button
                className="delivery-day-details notranslate"
                onClick={toggleDeliveryDateModal}
                type="button"
                disabled={deliveryDatesLoading}
              >
                <div className="delivery-day-details__selected-date">
                  <p className="delivery-day-details__selected-date__formatted-date">
                    {format(
                      parseISO(selectedDeliveryDate.value),
                      'E d LLL yy',
                      {
                        locale: dateLocale
                      }
                    )}
                  </p>

                  {selectedDeliveryDate.value ===
                    user.firstDeliverableDate.date && (
                    <span className="delivery-day-details__earliest-delivery-indicator">
                      {t(`delivery_date_button.earliest_delivery`)}
                    </span>
                  )}
                </div>
                <div
                  className={`delivery-day-details__change-date-button ${
                    deliveryDatesLoading
                      ? 'delivery-day-details__change-date-button--disabled'
                      : ''
                  }`}
                >
                  <div className="delivery-day-details__change-date-button__calendar">
                    <span className="delivery-day-details__change-date-button__calendar__date">
                      {format(parseISO(selectedDeliveryDate.value), 'd', {
                        locale: dateLocale
                      })}
                    </span>
                    <img
                      alt={t(`delivery_date_button.icon_alt`)}
                      className="delivery-day-details__selected-date__calendar__icon"
                      src={calendarIcon}
                    />
                  </div>
                  <div className="delivery-day-details__change-date-button__change">
                    <p>{t(`delivery_date_button.button_text`)}</p>
                    <img alt="" src={rightArrow} />
                  </div>
                </div>
              </button>
              {shouldSeeCourierSelection && (
                <DeliveryDateModalWithCourier
                  namespace={namespace}
                  toggleDeliveryDateModal={toggleDeliveryDateModal}
                  isDeliveryDateModalOpen={showDeliveryDateModal}
                  shippingCountryCode={shippingCountryCode}
                  preferredLanguage={preferredLanguage}
                  calendarDates={calendarDates}
                  setCalendarDates={setCalendarDates}
                  setSelectedDate={setSelectedDate}
                  setSelectedCarrier={setSelectedCarrier}
                  deliveryDateWithCourierLoading={
                    deliveryDateWithCourierLoading
                  }
                  deliveryDateWithCourierData={deliveryDateWithCourierData}
                  shouldSeeOptimisedCourierSelection={
                    shouldSeeOptimisedCourierSelection
                  }
                />
              )}
              {!shouldSeeCourierSelection && deliveryDateData && (
                <DeliveryDateModal
                  namespace={namespace}
                  openDeliveryDateModal={toggleDeliveryDateModal}
                  isDeliveryDateModalOpen={showDeliveryDateModal}
                  shippingCountryCode={shippingCountryCode}
                  availableDeliveryDates={deliveryDateData.calendarDates}
                  preferredLanguage={preferredLanguage}
                />
              )}
              {/* eslint-disable react/jsx-no-bind */}
              <MobileNumberInputField
                value={mobileNumber}
                dataTestId="checkout-mobile-input"
                onChange={(e) =>
                  updateDeliveryDetails('mobileNumber', e.currentTarget.value)
                }
                onBlur={() =>
                  validateInputField('mobileNumber', mobileNumber.value)
                }
                shouldSeeRequiredPhoneNumber={shouldSeeRequiredPhoneNumber}
                shippingCountryCode={shippingCountryCode}
              />
              {/* eslint-enable react/jsx-no-bind */}
              {shouldSeeDeliveryNotes && (
                <div className={STYLES.deliveryNotesWrapper}>
                  <Text
                    namespace={namespace}
                    text={`${copyContext}.deliverey_note`}
                    variant="display16"
                    align="left"
                  />
                  <MultiLineTextField
                    label="deliveryNotes"
                    alwaysShowLabel
                    rows={4}
                    maxLength={
                      selectedDeliveryDate.selectedDeliveryArea
                        ?.preferredCarrierService.deliveryNotesLimit ?? 30
                    }
                    optional
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(e) => updateDeliveryDetails('deliveryNotes', e)}
                    // eslint-disable-next-line react/jsx-no-bind
                    onValidate={() =>
                      validateInputField('deliveryNotes', deliveryNotes.value)
                    }
                    errorMessageCopy={deliveryNotes.errorMessage}
                  />
                </div>
              )}
            </div>
            <Notification
              text={{
                text: courierInfoCopy(),
                translate: false,
                align: 'left'
              }}
              variant="info"
            />
            {visible && (
              <div className="checkout__continue-button">
                <Button
                  typography={{
                    namespace,
                    text: requiresPaymentDetailsOnCheckout
                      ? 'continue_button.payment'
                      : 'continue_button.place_order'
                  }}
                  dataTestId="checkout-continue-to-payment-button"
                  disabled={!valid}
                  onClick={continueButtonClick}
                  fullWidth
                  identifier="continue_to_payment"
                  screenIdentifier="checkout_page"
                />
              </div>
            )}
          </Expand>
        </div>
      </Card>
    </section>
  )
}

export default DeliveryDateOptimisedCourier
